<template>
  <b-modal
    :id="modalId"
    :title-html="`<div class='modal-header-custom'><i class='fas fa-history mr-2'></i>Histórico do Equipamento: <span class='equipment-name'>${equipmentName}</span></div>`"
    size="xl"
    hide-footer
    modal-class="history-modal"
  >
  <!---->
    <!-- LOADING -->
    <div v-if="loading" class="text-center my-5 loading-container">
      <b-spinner variant="primary" label="Carregando..." class="custom-spinner"></b-spinner>
      <p class="mt-3 loading-text">Carregando histórico...</p>
    </div>

    <!-- ERRO -->
    <b-alert v-if="error" variant="danger" show class="custom-alert">
      <i class="fas fa-exclamation-triangle mr-2"></i>{{ error }}
    </b-alert>

    <!-- VAZIO -->
    <div v-if="!loading && !error && analyses.length === 0" class="text-center py-5 empty-state">
      <i class="fas fa-inbox fa-3x mb-3 empty-icon"></i>
      <p class="empty-text">Nenhum histórico encontrado.</p>
    </div>

    <!-- TABELA -->
    <div v-if="!loading && !error && analyses.length > 0" class="history-content">
      <div class="table-stats mb-3">
        <span class="stats-badge">
          <i class="fas fa-list-ul mr-2"></i>
          Total de registros: <strong>{{ analyses.length }}</strong>
        </span>
      </div>

      <b-table
        :items="paginatedItems"
        :fields="fields"
        striped
        hover
        responsive
        class="history-table"
      >
        <template #cell(id)="data">
          <span class="id-badge">#{{ data.item.id }}</span>
        </template>

        <template #cell(sampleId)="data">
          <span class="sample-badge">{{ data.item.sampleId }}</span>
        </template>

        <template #cell(startDate)="data">
          <div class="date-cell">
            <i class="far fa-calendar-alt mr-1"></i>
            {{ formatDate(data.item.startDate) }}
          </div>
        </template>

        <template #cell(endDate)="data">
          <div class="date-cell">
            <i class="far fa-calendar-check mr-1"></i>
            {{ formatDate(data.item.endDate) }}
          </div>
        </template>

        <!-- NOVO: Nome da Empresa -->
        <template #cell(companyName)="data">
          <div class="company-cell">
            <i class="fas fa-building mr-1"></i>
            {{ data.item.company?.name || '-' }}
          </div>
        </template>

        <!-- NOVO: Nome do Funcionário -->
        <template #cell(employeeName)="data">
          <div class="employee-cell">
            <i class="fas fa-user mr-1"></i>
            {{ data.item.employee?.name || '-' }}
          </div>
        </template>
      </b-table>

      <div class="pagination-container">
        <b-pagination
          v-model="page"
          :total-rows="analyses.length"
          :per-page="perPage"
          class="custom-pagination"
          first-number
          last-number
        ></b-pagination>
      </div>
    </div>
  </b-modal>
</template>

<script>
import { request } from '@/services'

export default {
  name: 'EquipmentHistoryModal',

  props: {
    modalId: { type: String, required: true },
    equipmentId: { type: Number, default: null },
    equipmentName: { type: String, default: '' }
  },

  data () {
    return {
      loading: false,
      error: null,
      analyses: [],
      fields: [
        { key: 'id', label: 'ID' },
        { key: 'sampleId', label: 'Amostra' },
        { key: 'startDate', label: 'Data Inicial' },
        { key: 'endDate', label: 'Data Final' },
        { key: 'companyName', label: 'Empresa' },
        { key: 'employeeName', label: 'Funcionário' }
      ],
      page: 1,
      perPage: 10
    }
  },

  computed: {
    paginatedItems () {
      const start = (this.page - 1) * this.perPage
      return this.analyses.slice(start, start + this.perPage)
    }
  },

  watch: {
    equipmentId (newValue) {
      if (newValue) {
        this.page = 1
        this.loadData()
      }
    }
  },

  methods: {
    async loadData () {
      this.loading = true
      this.error = null
      this.analyses = []

      request.get(
        `Analyses/equipment/${this.equipmentId}`,
        response => {
          this.analyses = response.data
          this.loading = false
        },
        error => {
          this.error = 'Erro ao carregar histórico.'
          this.loading = false
          console.error(error)
        }
      )
    },

    formatDate (dateString) {
      if (!dateString) return '-'
      return new Date(dateString).toLocaleDateString('pt-BR')
    }
  }
}
</script>

<style>
/* Global Modal Styles */
.history-modal .modal-dialog {
  max-width: 95%;
}

.history-modal .modal-content {
  border-radius: 15px;
  border: none;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.15);
}

.history-modal .modal-header {
  background: linear-gradient(135deg, #FF8C42 0%, #FF7A29 100%);
  color: white;
  border-top-left-radius: 15px;
  border-top-right-radius: 15px;
  border-bottom: none;
  padding: 20px 30px;
}

.history-modal .modal-title {
  font-weight: 700;
  font-size: 1.4rem;
}

.modal-header-custom {
  display: flex;
  align-items: center;
}

.equipment-name {
  color: #FFF5EF;
  font-weight: 800;
  text-shadow: 1px 1px 2px rgba(0, 0, 0, 0.1);
}

.history-modal .modal-body {
  padding: 30px;
  background-color: #f8f9fa;
}

.history-modal .close {
  color: white;
  opacity: 1;
  text-shadow: none;
  font-size: 1.5rem;
  font-weight: 300;
}

.history-modal .close:hover {
  color: #FFF5EF;
}
</style>

<style scoped>
/* Loading State */
.loading-container {
  padding: 40px;
}

.custom-spinner {
  width: 3rem;
  height: 3rem;
  border-width: 0.3rem;
}

.loading-text {
  font-size: 1.1rem;
  color: #6c757d;
  font-weight: 500;
}

/* Custom Alert */
.custom-alert {
  border-radius: 10px;
  border: none;
  font-weight: 600;
  padding: 15px 20px;
  box-shadow: 0 4px 15px rgba(189, 27, 36, 0.2);
}

/* Empty State */
.empty-state {
  padding: 60px 20px;
}

.empty-icon {
  color: #dee2e6;
  margin-bottom: 20px;
}

.empty-text {
  font-size: 1.2rem;
  color: #6c757d;
  font-weight: 500;
  margin: 0;
}

/* History Content */
.history-content {
  animation: fadeIn 0.4s ease-in;
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* Table Stats */
.table-stats {
  display: flex;
  justify-content: flex-end;
  margin-bottom: 20px;
}

.stats-badge {
  background: linear-gradient(135deg, #FF8C42 0%, #FF7A29 100%);
  color: white;
  padding: 10px 20px;
  border-radius: 25px;
  font-size: 0.95rem;
  font-weight: 600;
  box-shadow: 0 4px 15px rgba(255, 140, 66, 0.3);
  display: inline-flex;
  align-items: center;
}

/* History Table */
.history-table {
  background: white;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
}

.history-table >>> thead {
  background: linear-gradient(135deg, #2c3e50 0%, #34495e 100%);
  color: white;
}

.history-table >>> thead th {
  font-weight: 700;
  text-transform: uppercase;
  font-size: 0.8rem;
  letter-spacing: 1px;
  padding: 18px 20px;
  border: none;
  white-space: nowrap;
}

.history-table >>> tbody tr {
  transition: all 0.2s ease;
  border-bottom: 1px solid #e9ecef;
}

.history-table >>> tbody tr:hover {
  background-color: #FFF5EF;
  transform: scale(1.005);
  box-shadow: 0 2px 8px rgba(255, 140, 66, 0.1);
}

.history-table >>> tbody td {
  padding: 16px 20px;
  vertical-align: middle;
  font-size: 0.95rem;
  color: #495057;
}

/* Cell Badges and Styles */
.id-badge {
  background: linear-gradient(135deg, #6c757d 0%, #5a6268 100%);
  color: white;
  padding: 4px 12px;
  border-radius: 15px;
  font-weight: 700;
  font-size: 0.85rem;
  display: inline-block;
}

.sample-badge {
  background: linear-gradient(135deg, #17a2b8 0%, #138496 100%);
  color: white;
  padding: 6px 14px;
  border-radius: 6px;
  font-weight: 600;
  font-size: 0.9rem;
  display: inline-block;
}

.date-cell {
  color: #495057;
  font-weight: 500;
  display: flex;
  align-items: center;
  white-space: nowrap;
}

.date-cell i {
  color: #FF8C42;
  font-size: 1rem;
}

.company-cell {
  color: #2c3e50;
  font-weight: 600;
  display: flex;
  align-items: center;
}

.company-cell i {
  color: #FF8C42;
  font-size: 1rem;
}

.employee-cell {
  color: #2c3e50;
  font-weight: 600;
  display: flex;
  align-items: center;
}

.employee-cell i {
  color: #FF8C42;
  font-size: 1rem;
}

/* Pagination */
.pagination-container {
  display: flex;
  justify-content: center;
  margin-top: 30px;
}

.custom-pagination >>> .page-link {
  color: #FF8C42;
  border: 2px solid #FF8C42;
  border-radius: 8px;
  margin: 0 4px;
  font-weight: 600;
  padding: 8px 16px;
  transition: all 0.3s ease;
}

.custom-pagination >>> .page-link:hover {
  background-color: #FF8C42;
  color: white;
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(255, 140, 66, 0.3);
}

.custom-pagination >>> .page-item.active .page-link {
  background: linear-gradient(135deg, #FF8C42 0%, #FF7A29 100%);
  border-color: #FF8C42;
  color: white;
  box-shadow: 0 4px 15px rgba(255, 140, 66, 0.4);
}

.custom-pagination >>> .page-item.disabled .page-link {
  background-color: #e9ecef;
  border-color: #dee2e6;
  color: #6c757d;
}

/* Responsive Design */
@media (max-width: 768px) {
  .history-table >>> thead th {
    font-size: 0.7rem;
    padding: 12px 10px;
  }

  .history-table >>> tbody td {
    padding: 12px 10px;
    font-size: 0.85rem;
  }

  .stats-badge {
    font-size: 0.85rem;
    padding: 8px 15px;
  }

  .date-cell,
  .company-cell,
  .employee-cell {
    font-size: 0.85rem;
  }
}
</style>
